import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Box,
  Divider,
} from '@mui/material';

interface MarkdownRendererProps {
  content: string;
  assetBaseUrl?: string;
}

export default function MarkdownRenderer({ content, assetBaseUrl }: MarkdownRendererProps) {
  // Replace asset: references with actual URLs
  const processedContent = assetBaseUrl
    ? content.replace(/!\[([^\]]*)\]\(asset:([^)]+)\)/g, `![$1](${assetBaseUrl}/$2)`)
    : content;

  return (
    <Box
      sx={{
        '& img': {
          maxWidth: '100%',
          height: 'auto',
          borderRadius: 1,
          my: 2,
        },
        '& blockquote': {
          borderLeft: 4,
          borderColor: 'primary.main',
          pl: 2,
          ml: 0,
          my: 2,
          py: 0.5,
          bgcolor: 'action.hover',
          borderRadius: '0 4px 4px 0',
        },
        '& ul, & ol': {
          pl: 3,
        },
        '& li': {
          mb: 0.5,
        },
        '& pre': {
          my: 2,
          borderRadius: 1,
          overflow: 'auto',
        },
        '& code:not(pre code)': {
          bgcolor: 'action.hover',
          px: 0.75,
          py: 0.25,
          borderRadius: 0.5,
          fontSize: '0.875em',
          fontFamily: 'monospace',
        },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Headings
          h1: ({ children }) => (
            <Typography variant="h4" gutterBottom sx={{ mt: 3, mb: 2 }}>
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="h5" gutterBottom sx={{ mt: 3, mb: 1.5 }}>
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
              {children}
            </Typography>
          ),
          h4: ({ children }) => (
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              {children}
            </Typography>
          ),
          h5: ({ children }) => (
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 1.5, mb: 0.5 }}>
              {children}
            </Typography>
          ),
          h6: ({ children }) => (
            <Typography variant="body2" gutterBottom sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}>
              {children}
            </Typography>
          ),

          // Paragraphs
          p: ({ children }) => (
            <Typography variant="body1" paragraph>
              {children}
            </Typography>
          ),

          // Links
          a: ({ href, children }) => (
            <Link href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </Link>
          ),

          // Horizontal rule
          hr: () => <Divider sx={{ my: 3 }} />,

          // Tables
          table: ({ children }) => (
            <TableContainer component={Paper} sx={{ my: 2 }}>
              <Table size="small">{children}</Table>
            </TableContainer>
          ),
          thead: ({ children }) => <TableHead>{children}</TableHead>,
          tbody: ({ children }) => <TableBody>{children}</TableBody>,
          tr: ({ children }) => <TableRow>{children}</TableRow>,
          th: ({ children }) => (
            <TableCell sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
              {children}
            </TableCell>
          ),
          td: ({ children }) => <TableCell>{children}</TableCell>,

          // Code blocks with syntax highlighting
          code: ({ className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const isInline = !match && !className;

            if (isInline) {
              return <code {...props}>{children}</code>;
            }

            return (
              <SyntaxHighlighter
                style={oneDark}
                language={match?.[1] || 'text'}
                PreTag="div"
                customStyle={{
                  margin: 0,
                  borderRadius: '4px',
                }}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            );
          },

          // Strong/Bold
          strong: ({ children }) => (
            <Typography component="span" fontWeight="bold">
              {children}
            </Typography>
          ),

          // Emphasis/Italic
          em: ({ children }) => (
            <Typography component="span" fontStyle="italic">
              {children}
            </Typography>
          ),

          // Images
          img: ({ src, alt }) => (
            <Box
              component="img"
              src={src}
              alt={alt || ''}
              sx={{
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                mx: 'auto',
                borderRadius: 1,
              }}
            />
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </Box>
  );
}
